import { createUseStyles } from "react-jss";
import * as Mixins from "../../../theme/Mixins";
import { alpha } from "../../../theme/Mixins";

const railHeight = ({ theme, isHybridGrid }) =>
    isHybridGrid ? theme.SliderRailHeightSmall : theme.SliderRailHeightLarge;
const handleSize = ({ theme, isHybridGrid }) =>
    isHybridGrid ? theme.SliderHandleSizeSmall : theme.SliderHandleSizeLarge;

export default createUseStyles({
    labelContainer: {
        composes: "slider__label-container",
        display: "flex",
        flexDirection: "row",
    },
    label: {
        composes: "slider__label",
        flex: "auto",
        marginTop: 16,
        color: ({ theme }) => Mixins.foregroundColor(theme, Mixins.wrapperBackgroundColor(theme)),
    },
    labelLeft: {
        composes: "slider__label--left",
        textAlign: "left",
    },
    labelMiddle: {
        composes: "slider__label--middle",
        textAlign: "center",
    },
    labelRight: {
        composes: "slider__label--right",
        textAlign: "right",
    },
    sliderContainer: {
        composes: "slider__container",
        marginLeft: ({ isHybridGrid }) => (isHybridGrid ? 10 : 0),
        marginRight: ({ isHybridGrid }) => (isHybridGrid ? 10 : 0),
        marginTop: ({ hasImages }) => (hasImages ? 90 : 0),
        maxWidth: ({ theme, size }) => Mixins.inputMaxWidth(theme, size),
        minWidth: ({ theme, size }) => Mixins.inputMinWidth(theme, size),
        padding: [[0, 5]],
    },
    slider: {
        composes: "slider__compound",
        position: "relative",
        width: "100%",
        height: "6em",
    },
    rail: {
        backgroundColor: ({ theme }) => theme.ControlBackground,
        borderColor: ({ theme }) => Mixins.borderColor(theme, theme.ControlBackground),
        borderRadius: ({ theme }) => theme.BorderRadius / 4,
        borderStyle: "solid",
        borderWidth: ({ theme }) => theme.BorderWidth,
        bottom: 0,
        composes: "slider__rail",
        cursor: "pointer",
        height: railHeight,
        left: -5,
        margin: [["auto", 0]],
        marginLeft: ({ theme }) => -theme.BorderWidth,
        position: "absolute",
        right: -5,
        top: 0,
        zIndex: 1,
    },
    handleContainer: {
        composes: "slider__handle-container",
    },
    handle: {
        align: "center",
        backgroundColor: ({ theme }) => theme.SecondaryColor,
        border: 0,
        borderColor: ({ theme }) => Mixins.borderColor(theme, theme.SecondaryColor),
        borderRadius: "50%",
        borderStyle: "solid",
        borderWidth: ({ theme }) => theme.BorderWidth,
        bottom: 0,
        composes: "slider__handle",
        cursor: "pointer",
        height: ({ theme, isHybridGrid }) =>
            handleSize({ theme, isHybridGrid }) - theme.BorderWidth,
        left: ({ percent }) => `${percent}%`,
        margin: [["auto", 0]],
        marginLeft: ({ theme, isHybridGrid }) =>
            (handleSize({ theme, isHybridGrid }) / 2 + theme.BorderWidth) * -1,
        outline: "2px solid transparent",
        position: "absolute",
        textAlign: "center",
        top: 0,
        transition: "background 200ms, box-shadow 200ms, outline-color 200ms",
        userSelect: "none",
        width: ({ theme, isHybridGrid }) => handleSize({ theme, isHybridGrid }) - theme.BorderWidth,
        zIndex: 2,

        "&:focus": {
            outlineColor: ({ theme }) => alpha(theme.SecondaryColor, 0.5),
            outlineStyle: "solid",
            outlineWidth: "22px",
        },
    },
    handleActive: {
        composes: "slider__handle--active",
        backgroundColor: ({ theme }) =>
            Mixins.darken(theme.SecondaryColor, theme.ActiveHighlightValue),
    },
    handleLabelContainer: {
        bottom: "100%",
        color: "transparent",
        composes: "slider__handle-label-container",
        display: "inline-block",
        left: 0,
        marginLeft: "50%",
        position: "absolute",
        transform: "translateX(-50%)",
    },
    handleLabelContainerRegular: {
        composes: "slider__handle-label-container--regular",
        marginBottom: 16,
        padding: [[5, 16]],
    },
    handleLabelContainerHybridGrid: {
        composes: "slider__handle-label-container--hybrid-grid",
        marginBottom: 10,
        paddingTop: 2,
        paddingBottom: 0,
        paddingLeft: 8,
        paddingRight: 6,
    },
    handleLabelText: {
        color: ({ theme }) =>
            Mixins.foregroundColor(
                theme,
                Mixins.compoundBackgroundColor(theme, theme.PrimaryColor),
            ),
        composes: "slider__handle-label-text",
        left: 0,
        position: "absolute",
        textAlign: "center",
        top: 0,
        zIndex: 4,
    },
    handleLabelTextDiscrete: {
        color: ({ theme }) => Mixins.foregroundColor(theme, Mixins.wrapperBackgroundColor(theme)),
        composes: "slider__handle-label-text-discrete",
        position: "relative",
        textAlign: "center",
        zIndex: 4,
        top: "110%",
    },
    handleLabelTextRegular: {
        composes: "slider__handle-label-text--regular",
        fontSize: "1rem",
        padding: [[5, 16]],
    },
    handleLabelTextHybridGrid: {
        composes: "slider__handle-label-text--hybrid-grid",
        fontSize: 14,
        paddingTop: 2,
        paddingBottom: 0,
        paddingLeft: 8,
        paddingRight: 0,
    },
    handleLabelImageContainer: {
        boxSizing: "border-box",
        composes: "slider__handle-label-image-container",
        height: 84,
        margin: "auto",
        position: "relative",
        width: 150,
    },
    handleLabelImage: {
        backgroundImage: ({ imageUrl }) => `url("${imageUrl}")`,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        bottom: 0,
        composes: "slider__handle-label-image",
        left: 0,
        position: "absolute",
        right: 0,
        top: 0,
        zIndex: 2,
    },
    handleLabelBackground: {
        align: "center",
        background: ({ theme, isDiscrete }) =>
            Mixins.alpha(isDiscrete ? theme.ControlBackground : theme.PrimaryColor),
        borderRadius: ({ theme }) => theme.BorderRadius / 2,
        bottom: 0,
        boxShadow: ({ theme, isDiscrete }) =>
            `0 0 0 ${theme.BorderWidth}px ${Mixins.borderColor(
                theme,
                isDiscrete ? theme.ControlBackground : theme.PrimaryColor,
            )}`,
        composes: "slider__handle-label-background",
        left: 0,
        opacity: ({ theme }) => theme.ControlAlpha,
        position: "absolute",
        right: 0,
        top: 0,
        zIndex: 2,
    },
    handleLabelArrowBorder: {
        border: "8px solid transparent",
        borderTopColor: ({ theme, isDiscrete }) =>
            Mixins.borderColor(theme, isDiscrete ? theme.ControlBackground : theme.PrimaryColor),
        content: '""',
        left: "50%",
        marginLeft: -8,
        marginTop: ({ theme }) => theme.BorderWidth - 2,
        opacity: ({ theme }) => theme.ControlAlpha,
        position: "absolute",
        top: "100%",
        zIndex: 1,
    },
    handleLabelArrow: {
        border: "8px solid transparent",
        borderTopColor: ({ theme, isDiscrete }) =>
            isDiscrete ? theme.ControlBackground : theme.PrimaryColor,
        left: "50%",
        marginLeft: -8,
        marginTop: -2,
        opacity: ({ theme }) => theme.ControlAlpha,
        position: "absolute",
        top: "100%",
        zIndex: 3,
    },
    tickContainer: {
        composes: "slider__tick-container",
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        margin: [["auto", 0]],
        height: 10,
        cursor: "default",
        userSelect: "none",
        zIndex: 0,
    },
    tick: {
        composes: "slider__tick",
        position: "absolute",
        marginLeft: -0.5,
        top: "100%",
        marginTop: ({ theme }) => theme.BorderWidth,
        width: 1,
        height: 13,
        left: ({ percent }) => `${percent}%`,
        backgroundColor: ({ theme }) => Mixins.borderColor(theme, theme.ControlBackground),
    },
    tickLabel: {
        composes: "slider__tick-label",
        cursor: "pointer",
        position: "absolute",
        top: "100%",
        marginTop: 9,
        padding: [[9, 0]],
        overflowWrap: "anywhere",
        textAlign: "center",
        color: ({ theme }) => Mixins.foregroundColor(theme, Mixins.wrapperBackgroundColor(theme)),
    },
    tickLabelRegular: {
        composes: "slider__tick-label--regular",
        fontSize: 18,
        lineHeight: "18px",
        marginLeft: ({ count, percent }) => {
            if (percent === 0 && count > 1) {
                return -20;
            }
            if (percent === 100 && count > 1) {
                return 4;
            }
            return `${-(100 / count) / 2}%`;
        },
        width: ({ count, percent }) => {
            if ([0, 100].includes(percent) && count > 1) {
                return `${90 / count / 2}%`;
            }
            return `${100 / count}%`;
        },
        left: ({ count, percent }) => {
            if (percent === 100 && count > 1) {
                return `${percent - 70 / count / 2}%`;
            }
            return `${percent}%`;
        },
    },
    tickLabelHybridGrid: {
        composes: "slider__tick-label--hybrid-grid",
        fontSize: 14,
        lineHeight: "14px",
        marginLeft: ({ count }) => `${-(100 / count) / 2}%`,
        width: ({ count }) => `${100 / count}%`,
        left: ({ percent }) => `${percent}%`,
    },
    noAnswer: {
        composes: "slider__no-answer",
        marginTop: 6,
        outline: "none",
        "& > div": {
            flex: "initial",
        },
    },
    noAnswerLabel: {
        composes: "slider__no-answer__label",
        marginLeft: 10,
        userSelect: "none",
    },
});
